/* ---------------------------------------------------------------------------
   Options — the field shell's third tab, and the only screen that is new.

   What belongs here: who this device is signed in as, what it is holding that
   the register has not got yet, and the switches an inspector owns. What does
   not: anything that manages the register itself. Bulk import, model deletion,
   inventory editing and the change log are desk work, and a field app that
   offered them would be the desktop application with a tab bar.

   ONE RULE, AND IT IS THE WHOLE SCREEN. **Every figure on it is one this device
   can actually measure.** A panel claiming a district is downloaded because a
   button was pressed once, rather than because the tiles are present, is the
   same failure as a register answering "fine" about a structure it knows nothing
   about — an answer somebody acts on, in place of a gap somebody could fill.

   That rule is why some things a settings screen would normally show are absent
   rather than approximated. There is no cache size and no "downloaded data"
   figure: reading Cache Storage is asynchronous, nothing in state holds the
   answer yet, and a number typed here from a button press would be a claim about
   the device made without asking it. See FA-04 in FIELD.md.
   --------------------------------------------------------------------------- */

import { h, ICONS } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { button, card, cardHead, fact, seg } from '../components/widgets.ts';
import { getRegister } from '../data/register.ts';
import { LOCALES, type Locale } from '../data/locale.ts';

import { installRoute, unregisterWorker } from '../core/install.ts';
import { ROLE_COPY, rolesLabel } from '../data/roles.ts';
import { agency } from '../tenant/agency.ts';
import { calendarsPanel } from './calendars.ts';
import { active } from '../tenant/registry.ts';

/** How the locales name themselves, for a picker somebody uses in their own language. */
const LOCALE_LABEL: Record<Locale, string> = { en: 'English', ne: 'नेपाली' };

/**
 * Who an inspection recorded now would be attributed to.
 *
 * Three different answers, and they are not interchangeable. A verified email is
 * a person. A service token is a machine, and saying "signed in" of one would be
 * wrong. No identity at all means this deployment does not enforce Access — which
 * is a fact about the deployment, not about the user, and is worth reading as
 * such on a device that is about to record something against a name.
 */
function identityLine(s: Readonly<State>): string {
  if (s.identity?.email) return s.identity.email;
  if (s.identity?.serviceToken) return `Service token · ${s.identity.serviceToken}`;
  return s.backend === 'live' ? 'Access not enforced on this deployment' : '—';
}

/** Signed in as, and what that means for what gets written. */
function profileCard(s: Readonly<State>): HTMLElement {
  const roles = s.identity?.roles ?? null;

  return card(
    { pad: true },
    cardHead('Profile'),
    fact('Signed in as', identityLine(s)),
    fact('Entries recorded as', s.identity?.author ?? '—'),
    /* WHAT THIS DEVICE MAY DO, and it is printed rather than left to a tooltip.
       The desk shell puts the role under the signed-in name and explains it on
       hover; there is no hover on a phone, so here the sentence itself is on the
       screen. It is also the answer to the commonest question this application
       will be asked in the field — "the button I had yesterday has gone" — and
       the guide sends people to this card for it. */
    fact('Your roles', roles ? rolesLabel(roles, s.identity?.suspended) : '—'),
    ...(roles && !s.identity?.suspended
      ? roles.map((role) => h('p', { class: 'opt__note', text: ROLE_COPY[role].who }))
      : []),
    /* CHANGING YOUR OWN PASSWORD, on the screen that already says who you are.
       It is here rather than on a screen of its own because it belongs with the
       identity it changes, and because Options is the one screen both shells
       draw — an inspector with a district phone has no other way to reach it. */
    s.identity
      ? h(
          'form',
          {
            class: 'opt__pw',
            onsubmit: (e: Event) => {
              e.preventDefault();
              void actions.submitPasswordChange();
            },
          },
          h('div', { class: 'opt__label', text: 'Change your password' }),
          h('input', {
            class: 'input',
            type: 'password',
            placeholder: 'Current password',
            autocomplete: 'current-password',
            value: s.passwordCurrent,
            oninput: (e: Event) =>
              actions.setPasswordField('passwordCurrent', (e.target as HTMLInputElement).value),
          }),
          h('input', {
            class: 'input',
            type: 'password',
            placeholder: 'New password',
            autocomplete: 'new-password',
            value: s.passwordNext,
            oninput: (e: Event) =>
              actions.setPasswordField('passwordNext', (e.target as HTMLInputElement).value),
          }),
          s.passwordError ? h('p', { class: 'opt__warn', text: s.passwordError }) : null,
          s.passwordDone ? h('p', { class: 'opt__note', text: s.passwordDone }) : null,
          button(s.passwordBusy ? 'Saving…' : 'Change password', {
            block: true,
            tall: true,
            type: 'submit',
            disabled: s.passwordBusy,
          }),
        )
      : null,

    h(
      'div',
      { class: 'opt__row' },
      button('Sign out', {
        block: true,
        tall: true,
        title: 'End this browser’s session',
        /* Through the action rather than by setting `location` here, so the
           places that offer this cannot drift on what signing out means — and
           because it is a request now, not a navigation: the session row is
           deleted server-side, since a session nobody deleted is one that still
           works. */
        onClick: () => void actions.signOut(),
      }),
    ),
  );
}

/**
 * What this device is holding, and the button that sends it.
 *
 * The queue is the most important thing on this screen and the reason the screen
 * exists at all: an inspector needs to know that what they recorded at the
 * structure is still only on their phone. The title bar's pill says the same
 * thing in three words; this says it with the counts and the last error.
 */
function syncCard(s: Readonly<State>): HTMLElement {
  const n = s.queued.length;
  const failed = s.queued.filter((w) => w.lastError).length;

  return card(
    { pad: true },
    cardHead('Data & sync'),
    /* WHICH DEPARTMENT'S REGISTER THIS IS, first, and above where it came from.
       One build serves several road authorities, and an inspector on a shared
       device needs to be able to check in one place that they are looking at
       their own office's structures before they record anything against one. */
    fact('Register', active().name),
    fact(
      'Records',
      s.backend === 'live'
        ? `${getRegister().length} from D1`
        : `${getRegister().length}, from this device's copy`,
    ),
    fact('Connection', s.backend === 'live' ? 'Reachable' : s.backendNote || 'Unreachable'),
    /* Where the bundled copy came from, shown only when it is the one on screen.
       Every department's seed has a different provenance and a different worth —
       five records off as-built drawings against 108 off a market-sounding pack —
       and a register whose origin is a desk assessment must not look like one
       transcribed from drawings. */
    s.backend === 'live'
      ? null
      : h('p', { class: 'opt__note', text: active().registerProvenance }),
    fact('Waiting to sync', n === 0 ? 'Nothing' : `${n} ${n === 1 ? 'entry' : 'entries'}`),
    failed ? fact('Last attempt failed for', `${failed} of ${n}`) : null,
    s.syncNote ? fact('Last sync', s.syncNote) : null,
    n
      ? h(
          'div',
          { class: 'opt__row' },
          button(s.syncing ? 'Sending…' : `Send ${n} now`, {
            primary: true,
            block: true,
            tall: true,
            disabled: s.syncing || s.backend !== 'live',
            title:
              s.backend === 'live'
                ? 'Send everything recorded on this device to the register'
                : 'Needs a connection',
            onClick: () => void actions.syncQueue(),
          }),
        )
      : null,
  );
}

/**
 * Install this application on this device.
 *
 * THE BUTTON EXISTS ONLY WHERE THE BROWSER HAS AGREED TO HONOUR IT, which is the
 * same rule as the rest of this screen: `canInstall` is a captured
 * `beforeinstallprompt`, not a guess about the platform. A button drawn without
 * one would do nothing when pressed, and a control that does nothing is read as
 * an application that is broken — see src/core/install.ts.
 *
 * So the card has three states and says something in all of them. Installed:
 * it is on this device already, and there is nothing to press. Offered: the
 * button. Neither: the menu that holds it on this browser, because "not offered"
 * covers iOS Safari, Firefox and an app that is already on the home screen, and
 * on none of those is the answer that it cannot be installed.
 */
function installCard(s: Readonly<State>): HTMLElement {
  const route = installRoute();

  return card(
    { pad: true },
    cardHead('Install on this device'),

    s.installed
      ? h('p', {
          class: 'opt__note',
          text: 'Installed on this device.',
        })
      : null,

    /* The note before the button, not after it. It is only ever there because
       something has already been pressed, and the outcome of that press is what
       somebody is looking for when they come back to this card. */
    s.installNote ? h('p', { class: 'opt__note', text: s.installNote }) : null,

    !s.installed && s.canInstall
      ? h(
          'div',
          { class: 'opt__row' },
          button('Install on this device', {
            primary: true,
            block: true,
            tall: true,
            iconPath: ICONS.install,
            title: 'Add Bridge Watch to the home screen',
            onClick: () => void actions.promptInstall(),
          }),
        )
      : null,

    !s.installed && !s.canInstall
      ? h('p', {
          class: 'opt__note',
          text:
            route === 'ios'
              ? 'Press Share, then Add to Home Screen.'
              : 'Use the browser menu: Install app, or Add to Home screen.',
        })
      : null,
  );
}

/**
 * The switches. Shell, language, theme.
 *
 * The shell toggle is here rather than anywhere else because it is the one
 * control that can make the rest of the application unreachable, and Options is
 * the screen both shells can draw. Somebody who switches to the desk shell on a
 * phone gets the desktop layout and can switch back from the same place.
 */
function deviceCard(s: Readonly<State>): HTMLElement {
  return card(
    { pad: true },
    cardHead('This device'),
    h(
      'div',
      { class: 'opt__field' },
      h('span', { class: 'opt__label', text: 'Interface' }),
      seg([
        {
          label: 'Field',
          on: s.shell === 'field',
          title: 'Three tabs, for use at a structure',
          onClick: () => actions.shellChosen('field'),
        },
        {
          label: 'Desktop',
          on: s.shell === 'desk',
          title: 'The full register, with every screen',
          onClick: () => actions.shellChosen('desk'),
        },
      ]),
    ),
    h(
      'div',
      { class: 'opt__field' },
      h('span', { class: 'opt__label', text: 'Language' }),
      seg(
        LOCALES.map((code) => ({
          label: LOCALE_LABEL[code],
          on: s.locale === code,
          onClick: () => actions.localeSelected(code),
        })),
      ),
    ),
    h(
      'div',
      { class: 'opt__field' },
      h('span', { class: 'opt__label', text: 'Theme' }),
      seg([
        { label: 'Light', on: s.theme === 'light', onClick: () => actions.setTheme('light') },
        { label: 'Dark', on: s.theme === 'dark', onClick: () => actions.setTheme('dark') },
      ]),
    ),
  );
}

/**
 * The support card, and the one destructive button in the field shell.
 *
 * "Reset offline storage" is `?sw=off` with a label. It exists here for the same
 * reason the URL exists at all: the normal remedy for a bad service worker is
 * "clear site data in developer tools", and an inspector on a district phone will
 * not be doing that. Having it on a screen means the fix is a sentence rather
 * than a deploy — and it is worded as what it costs, not as what it fixes.
 */
function supportCard(s: Readonly<State>): HTMLElement {
  const n = s.queued.length;

  return card(
    { pad: true },
    cardHead('Help & support'),

    /* THE GUIDE, FIRST IN THE CARD THAT IS NAMED AFTER IT. This is the field
       shell's only way in — there is no fourth tab for it, the same way there is
       no tab for the bridge record — so it goes above the reset rather than
       below, where somebody scrolling for help would meet the destructive button
       first. See FIELD_SCREENS in src/core/shell.ts for why the field shell
       reaches this screen at all. */
    h(
      'div',
      { class: 'opt__row' },
      button('Open the guide', {
        primary: true,
        block: true,
        tall: true,
        iconPath: ICONS.book,
        title: 'How to use the field app and the web app',
        onClick: () => actions.go('guide'),
      }),
    ),

    n
      ? h('p', {
          class: 'opt__warn',
          text:
            `${n} ${n === 1 ? 'entry has' : 'entries have'} not reached the register yet. ` +
            'Send them before resetting — a reset cannot bring them back.',
        })
      : null,
    h(
      'div',
      { class: 'opt__row' },
      button('Reset offline storage', {
        danger: true,
        block: true,
        tall: true,
        iconPath: ICONS.refresh,
        title: 'Clear cached data on this device and reload',
        onClick: () => {
          void unregisterWorker().then(() => window.location.reload());
        },
      }),
    ),
  );
}

export function optionsScreen(s: Readonly<State>): HTMLElement {
  return h(
    'div',
    { class: 'opt' },
    profileCard(s),
    /* The department's working calendars (LC-43): read by all, kept by the
       administrator. */
    s.shell === 'desk' ? calendarsPanel(s) : null,
    syncCard(s),
    installCard(s),
    deviceCard(s),
    supportCard(s),
    /* The agency block, not a translated sentence. Same source the printed sheet
       and the desk header read, so there is one place this is written down. */
    h('p', {
      class: 'opt__foot',
      text: `${agency().department} · ${agency().branch}`,
    }),
  );
}
